import { useReducedMotion } from "@mantine/hooks";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import { useIsDark } from "@/hooks/useIsDark";
import { i18nStore } from "@/store/i18n";
import { toOrbParamRecord } from "@/types/ai-assistant-orb";
import type { AssistantOrbProps } from "./assistant-orb.types";
import { buildColorRamp, sampleRamp } from "./orb-colors";
import { subscribeToOrbLoop } from "./orb-loop";
import {
	idleBrightness,
	isIdleFrozen,
	pixelShift,
	shouldAnimateOrb,
	targetFps,
} from "./orb-motion";
import { renderOrbFrame } from "./orb-renderers";
import { OrbStatic } from "./orb-static";
import { resolveOrbColors } from "./orb-theme";

/** Batas device pixel ratio: di atas 2 biayanya naik tanpa beda yang terlihat. */
const MAX_DPR = 2;

const resolveDpr = (): number =>
	typeof window === "undefined"
		? 1
		: Math.min(MAX_DPR, Math.max(1, window.devicePixelRatio || 1));

/**
 * Logo Jenna sebagai orb animasi, digambar di Canvas 2D tanpa dependency.
 *
 * Komponen ini selalu merender SVG diam lebih dulu, lalu menggantinya dengan
 * kanvas hanya bila konteks 2D benar-benar didapat. Itu bukan sekadar
 * kehati-hatian: `happy-dom` di lingkungan test menyediakan `getContext` tapi
 * mengembalikan `null`, dan sebagian browser memblokir kanvas demi privasi.
 *
 * Dipakai langsung untuk varian particle-sphere/wireframe-mesh/glow-ring, DAN
 * sebagai fallback varian liquid-glass (dipaksa variant="glow-ring" oleh
 * AssistantOrb) saat WebGL2 tidak tersedia — lihat assistant-orb.tsx.
 */
export function AssistantCanvas2dOrb({
	config,
	name,
	size,
	idleMs = 0,
	forceLight,
}: AssistantOrbProps) {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [canvasReady, setCanvasReady] = useState(false);
	const reducedMotion = useReducedMotion();
	const { animasiTransisi } = useSnapshot(i18nStore);
	// useId() bisa memuat karakter yang tidak sah di dalam url(#…) pada SVG.
	const rawId = useId();
	const gradientId = `orb-grad-${rawId.replace(/[^a-zA-Z0-9_-]/g, "")}`;

	const themeIsLight = !useIsDark();
	const light = forceLight ?? themeIsLight;
	const colors = resolveOrbColors(config, light);
	const ramp = useMemo(
		() => buildColorRamp(colors.from, colors.to, light),
		[colors.from, colors.to, light],
	);
	const params = useMemo(
		() => toOrbParamRecord(config.variant, config.params),
		[config.variant, config.params],
	);

	// Sisi render sebenarnya; "100%" diukur dari kotak kanvas setelah layout.
	const cssSize = typeof size === "number" ? size : null;

	const animate = shouldAnimateOrb(reducedMotion, animasiTransisi);
	const frozen = isIdleFrozen(idleMs);
	const brightness = idleBrightness(idleMs);

	// Nilai yang berubah tiap frame dibaca lewat ref agar loop tidak perlu
	// didaftarkan ulang setiap kali salah satunya bergeser.
	const frameRef = useRef({ ramp, params, light, brightness, animate, frozen });
	frameRef.current = { ramp, params, light, brightness, animate, frozen };

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		if (!ctx) {
			setCanvasReady(false);
			return;
		}
		setCanvasReady(true);

		const draw = (elapsedMs: number): void => {
			const state = frameRef.current;
			const rect = canvas.getBoundingClientRect();
			const side = cssSize ?? Math.round(rect.width);
			if (!Number.isFinite(side) || side <= 0) return;
			const dpr = resolveDpr();
			const pixels = Math.round(side * dpr);
			if (canvas.width !== pixels || canvas.height !== pixels) {
				canvas.width = pixels;
				canvas.height = pixels;
			}
			const still = !state.animate || state.frozen;
			renderOrbFrame(
				ctx,
				pixels,
				pixels,
				config.variant,
				{
					ramp: state.ramp,
					params: state.params,
					time: still ? 0 : elapsedMs / 1000,
					cssSize: side,
					light: state.light,
					brightness: state.brightness,
				},
				still ? { x: 0, y: 0 } : pixelShift(elapsedMs),
			);
		};

		// Gerak dimatikan: gambar satu frame diam, jangan daftarkan loop sama sekali.
		if (!animate || frozen) {
			draw(0);
			return;
		}
		return subscribeToOrbLoop({ fps: targetFps(cssSize ?? 56), draw });
	}, [config.variant, cssSize, animate, frozen]);

	const style = {
		width: size,
		height: size,
		display: "block",
		flexShrink: 0,
	} as const;

	return (
		<>
			{!canvasReady && (
				<OrbStatic
					name={name}
					size={size}
					from={sampleRamp(ramp, 0)}
					to={sampleRamp(ramp, 1)}
					gradientId={gradientId}
				/>
			)}
			<canvas
				ref={canvasRef}
				role="img"
				aria-label={name}
				data-assistant-orb="canvas"
				style={canvasReady ? style : { ...style, display: "none" }}
			/>
		</>
	);
}
